import type { DocumentCoherenceDto, GetDocumentExtractionOutput } from 'korint-sdk';
import type { DocumentClassificationState } from 'korint-sdk/react';
import { isUnresolvedCategory } from '@folio/utils/document-categories';
import {
  isDocumentProcessing,
  parseAIClassificationMetadata,
} from '@folio/components/features/document-viewer';
import type {
  DocumentReview,
  DocumentReviewReason,
} from '@folio/components/features/document-viewer/types';

export type DocumentReviewStatus = DocumentReview['status'];

export type DocumentReviewFilter = 'all' | 'attention' | 'missing';

export type DocumentReviewSummary = {
  receivedCount: number;
  missingRequiredCount: number;
  attentionCount: number;
  processingCount: number;
  isLoading: boolean;
  hasError: boolean;
};

export function getDocumentReview({
  document,
  classification,
  extraction,
  validity,
  validityEnabled,
  coherence,
  requiresConformityDecision = false,
}: {
  document: { id: string; category?: string | null; metadata: Record<string, unknown> };
  classification?: DocumentClassificationState;
  extraction?: GetDocumentExtractionOutput;
  validity?: { status: string };
  validityEnabled: boolean;
  coherence?: Pick<DocumentCoherenceDto, 'status'>;
  requiresConformityDecision?: boolean;
}): DocumentReview {
  if (
    document.id.startsWith('optimistic-') ||
    isDocumentProcessing({
      classification,
      extractionStatus: extraction?.status,
      validityStatus: validity?.status,
    })
  ) {
    return { status: 'processing', reasons: [] };
  }

  const category = String(document.category ?? '');
  const metadata = parseAIClassificationMetadata(document.metadata.aiclassification);
  const reasons: DocumentReviewReason[] = [];
  if (isUnresolvedCategory(category) || metadata?.status === 'pending_review') {
    reasons.push('classification');
  }
  if (extraction?.status === 'FAILED' || classification?.status === 'error') {
    reasons.push('analysis_failed');
  }
  if (classification?.status === 'scan_infected') {
    reasons.push('unsafe_document');
  }
  if (validityEnabled && validity && validity.status !== 'VALID' && validity.status !== 'SKIPPED') {
    reasons.push(validity.status === 'INVALID' ? 'validity' : 'validity_unavailable');
  }
  if (coherence?.status === 'INCOHERENT') {
    reasons.push('coherence');
  }
  if (requiresConformityDecision) {
    reasons.push('conformity');
  }

  return {
    status:
      reasons.length > 0 ? 'attention' : validity?.status === 'VALID' ? 'checked' : 'received',
    reasons,
  };
}
